import { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { TableCellsIcon, ListBulletIcon } from '@heroicons/react/24/outline';

const ALL_DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
const COLORS   = ['#3b6ef8','#10c98e','#8b5cf6','#f59e0b','#ef4444','#06b6d4','#ec4899','#14b8a6','#f97316','#6366f1'];
const DAY_DOW  = { Monday:1, Tuesday:2, Wednesday:3, Thursday:4, Friday:5, Saturday:6, Sunday:0 };

const subjColor = (s, arr) => COLORS[arr.indexOf(s) >= 0 ? arr.indexOf(s) % COLORS.length : 0];

const fmtShort = (d) => d
  ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short' })
  : '—';

const css = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
.ttr * { box-sizing:border-box; }
.ttr { font-family:'Sora',sans-serif; color:#1a1d2e; animation:ttrFade .4s ease both; }
@keyframes ttrFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
.ttr-hdr { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
.ttr-title { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
.ttr-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }
.vtoggle { display:flex; gap:3px; background:#f0f2f7; border-radius:10px; padding:3px; }
.vbtn { display:inline-flex; align-items:center; gap:5px; padding:6px 12px; border-radius:8px; border:none; font-family:'Sora',sans-serif; font-size:.76rem; font-weight:600; cursor:pointer; background:transparent; color:#9094a8; transition:all .15s; }
.vbtn svg { width:13px; height:13px; stroke-width:2; }
.vbtn.on { background:white; color:#0f1123; box-shadow:0 1px 4px rgba(0,0,0,.1); }
.dtabs { display:flex; gap:4px; margin-bottom:1.2rem; overflow-x:auto; padding-bottom:4px; scrollbar-width:none; }
.dtabs::-webkit-scrollbar { display:none; }
.dtab { padding:5px 14px; border-radius:8px; border:1.5px solid #e0e4f0; background:white; font-family:'Sora',sans-serif; font-size:.74rem; font-weight:600; cursor:pointer; color:#9094a8; white-space:nowrap; flex-shrink:0; transition:all .15s; }
.dtab:hover { border-color:#10c98e; color:#10c98e; }
.dtab.on { background:#ecfdf5; border-color:#10c98e; color:#10c98e; }
.dtab.off-tab { border-color:#fecaca; color:#ef4444; background:#fef2f2; }
.dtab.off-tab.on { background:#ef4444; color:white; border-color:#ef4444; }
.dtab.hol-tab { border-color:#a7f3d0; color:#10c98e; background:#ecfdf5; }
.panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1rem; }
.panel-hdr { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
.panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
/* List table */
.lt { width:100%; border-collapse:collapse; }
.lt thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
.lt th { padding:10px 16px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
.lt tbody tr { border-bottom:1px solid #f3f5fb; transition:background .12s; }
.lt tbody tr:last-child { border-bottom:none; }
.lt tbody tr:hover { background:#fafbff; }
.lt td { padding:11px 16px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
.lt tr.off-row td { background:#fef9f9; }
.lt tr.hol-row td { background:#f0fdf4; }
.lt tr.sun-row td { background:#f8f9fc; }
.pbadge { display:inline-flex; align-items:center; justify-content:center; width:28px; height:28px; border-radius:8px; background:#ecfdf5; color:#10c98e; font-size:.76rem; font-weight:700; font-family:'JetBrains Mono',monospace; }
.dbadge { display:inline-flex; font-size:.72rem; font-weight:600; padding:3px 9px; border-radius:99px; }
.tcell  { font-size:.74rem; font-family:'JetBrains Mono',monospace; color:#6b7194; white-space:nowrap; }
.class-tag { display:inline-flex; font-size:.72rem; font-weight:600; padding:3px 9px; border-radius:99px; background:#eef2ff; color:#3b6ef8; margin-left:4px; }
/* Grid */
.gwrap { overflow-x:auto; -webkit-overflow-scrolling:touch; width:100%; }
.gt { border-collapse:collapse; min-width:600px; table-layout:fixed; }
.gt th { padding:10px 12px; font-size:.67rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; background:#fafbff; border:1px solid #edf0f9; text-align:center !important; white-space:nowrap; }
.gt th.dh { background:#f0f2f7; text-align:left; padding-left:14px; width:100px; min-width:90px; color:#6b7194; }
.gt th.lh { background:#fffbeb; color:#92400e; min-width:80px; }
.gt td { border:1px solid #edf0f9; padding:6px 8px; vertical-align:top; width:130px; min-width:110px; height:72px; text-align:center !important; }
.gt td.dc     { background:#f8f9fc; font-weight:700; font-size:.82rem; color:#0f1123; white-space:nowrap; vertical-align:middle; padding:10px 14px; border-right:2px solid #e8ebf4; }
.gt td.off-dc { background:#fef9f9; color:#ef4444; font-weight:700; font-size:.82rem; vertical-align:middle; padding:10px 14px; border-right:2px solid #e8ebf4; }
.gt td.hol-dc { background:#f0fdf4; color:#10c98e; font-weight:700; font-size:.82rem; vertical-align:middle; padding:10px 14px; border-right:2px solid #e8ebf4; }
.gt td.sun-dc { background:#f8f9fc; color:#9094a8; font-weight:700; font-size:.82rem; vertical-align:middle; padding:10px 14px; border-right:2px solid #e8ebf4; }
.gt td.lc { background:#fffbeb; text-align:center; vertical-align:middle; }
.gt td.ec { text-align:center; color:#e0e4f0; font-size:.8rem; vertical-align:middle; }
.gt td.off-cell { background:#fef9f9; vertical-align:middle; text-align:center; }
.gt td.hol-cell { background:#f0fdf4; vertical-align:top; padding:6px 8px; }
.gt td.sun-cell { background:#f8f9fc; vertical-align:middle; text-align:center; }
.ge { border-radius:8px; padding:6px 8px; margin-bottom:3px; }
.ge:last-child { margin-bottom:0; }
.ge-s  { font-size:.76rem; font-weight:700; line-height:1.2; }
.ge-t  { font-size:.66rem; margin-top:2px; }
.ge-tm { font-size:.62rem; font-family:'JetBrains Mono',monospace; margin-top:2px; }
.hol-chip { display:flex; align-items:center; gap:5px; padding:5px 8px; border-radius:8px; background:#dcfce7; border-left:3px solid #10c98e; margin-bottom:3px; }
.hol-chip-name { font-size:.72rem; font-weight:700; color:#065f46; }
.hol-chip-date { font-size:.62rem; color:#10c98e; font-family:'JetBrains Mono',monospace; }
/* Mobile cards */
.mob-grid { display:none; }
.lt-cards { display:none; }
.day-card { background:white; border-radius:12px; border:1px solid #e8ebf4; margin-bottom:10px; overflow:hidden; }
.day-card.off-card { border-color:#fecaca; }
.day-card.hol-card { border-color:#a7f3d0; }
.day-card.sun-card { border-color:#e0e4f0; }
.day-card-hdr { display:flex; align-items:center; justify-content:space-between; padding:10px 14px; background:#f8f9fc; border-bottom:1px solid #edf0f9; }
.day-card.off-card .day-card-hdr { background:#fef9f9; }
.day-card.hol-card .day-card-hdr { background:#f0fdf4; }
.day-card.sun-card .day-card-hdr { background:#f8f9fc; }
.day-card-name  { font-size:.86rem; font-weight:700; color:#0f1123; }
.day-card-badge { font-size:.7rem; font-weight:600; padding:2px 8px; border-radius:99px; }
.period-pill { display:flex; align-items:center; gap:10px; padding:10px 14px; border-bottom:1px solid #f3f5fb; }
.period-pill:last-child { border-bottom:none; }
.period-num  { display:inline-flex; align-items:center; justify-content:center; width:26px; height:26px; border-radius:7px; background:#ecfdf5; color:#10c98e; font-size:.72rem; font-weight:700; font-family:'JetBrains Mono',monospace; flex-shrink:0; }
.period-info { flex:1; }
.period-subj { font-size:.82rem; font-weight:700; }
.period-meta { font-size:.7rem; color:#6b7194; margin-top:2px; }
.lunch-pill { display:flex; align-items:center; gap:8px; padding:8px 14px; background:#fffbeb; border-bottom:1px solid #fde68a; font-size:.78rem; font-weight:600; color:#92400e; }
.off-pill  { padding:12px 14px; font-size:.78rem; color:#ef4444; font-weight:600; background:#fef9f9; }
.sun-pill  { padding:12px 14px; font-size:.78rem; color:#9094a8; font-style:italic; background:#f8f9fc; }
.hol-pill  { padding:8px 14px; font-size:.78rem; background:#f0fdf4; border-bottom:1px solid #a7f3d0; }
.ttr-empty   { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
.ttr-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
.spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#10c98e; border-radius:50%; animation:spin .7s linear infinite; }
@keyframes spin { to{transform:rotate(360deg)} }
@media(max-width:768px) {
  .ttr-title { font-size:1.1rem; }
  .gwrap { display:none; }
  .mob-grid { display:block; }
  .lt-wrap { display:none !important; }
  .lt-cards { display:block; padding:10px; }
}
`;

const TeacherTimetable = () => {
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [activeDay, setActiveDay] = useState('All');
  const [viewMode,  setViewMode]  = useState('list');

  useEffect(() => {
    api.get('/teacher/timetable')
      .then(r => { setData(r.data); setViewMode(r.data?.timetableView || 'list'); })
      .catch(() => toast.error('Failed to load timetable'))
      .finally(() => setLoading(false));
  }, []);

  const periods      = data?.periods        || [];
  const lunchStart   = data?.lunchBreakStart || '';
  const lunchEnd     = data?.lunchBreakEnd   || '';
  const weeklyOff    = data?.weeklyOffDays   || [0];
  const satPolicy    = data?.saturdayPolicy  || 'all_off';
  const holidays     = data?.holidays        || [];

  // ── Off-day helpers ────────────────────────────────────────────────────────
  const isOffDay = (dayName) => {
    if (dayName === 'Sunday')   return true;
    if (dayName === 'Saturday') return satPolicy === 'all_off';
    const dow = DAY_DOW[dayName];
    return (weeklyOff || []).filter(d => d !== 6).includes(dow);
  };

  const satNote = (dayName) => {
    if (dayName !== 'Saturday') return null;
    if (satPolicy === 'alternate_odd_off')  return '1st & 3rd off';
    if (satPolicy === 'alternate_even_off') return '2nd & 4th off';
    return null;
  };

  // ── Holidays grouped by day-of-week ───────────────────────────────────────
  const holidaysByDay = {};
  holidays.forEach(h => {
    const dow = new Date(h.date).getDay();
    const day = ALL_DAYS.find(d => DAY_DOW[d] === dow);
    if (day) {
      if (!holidaysByDay[day]) holidaysByDay[day] = [];
      holidaysByDay[day].push(h);
    }
  });

  const getDayType = (day) => {
    if (day === 'Sunday')  return 'sunday';
    if (isOffDay(day))     return 'off';
    if (holidaysByDay[day]?.length) return 'holiday';
    return 'normal';
  };

  const uniqSubjs = [...new Set(periods.map(e => e.subject).filter(Boolean))];

  // Grid setup
  const allPeriods = [...new Set(periods.map(e => e.period))].sort((a,b) => a-b);
  const gridMap    = {};
  periods.forEach(e => {
    if (!gridMap[e.period]) gridMap[e.period] = {};
    if (!gridMap[e.period][e.day]) gridMap[e.period][e.day] = [];
    gridMap[e.period][e.day].push(e);
  });

  // Lunch insertion
  const lunchAfterPeriod = (() => {
    if (!lunchStart || !allPeriods.length) return null;
    const [lh, lm] = lunchStart.split(':').map(Number);
    for (let i = 0; i < allPeriods.length - 1; i++) {
      const cur = periods.find(e => e.period === allPeriods[i]   && e.endTime);
      const nxt = periods.find(e => e.period === allPeriods[i+1] && e.startTime);
      if (cur && nxt) {
        const [ch, cm] = cur.endTime.split(':').map(Number);
        const [nh, nm] = nxt.startTime.split(':').map(Number);
        if (ch*60+cm <= lh*60+lm && lh*60+lm <= nh*60+nm) return allPeriods[i];
      }
    }
    return null;
  })();

  const cols = [];
  allPeriods.forEach((p, i) => {
    cols.push({ t:'p', p });
    if (lunchAfterPeriod === p) cols.push({ t:'l' });
  });

  const periodTime = (p) => {
    const e = periods.find(x => x.period === p && x.startTime);
    if (!e) return null;
    return e.endTime ? `${e.startTime}–${e.endTime}` : e.startTime;
  };

  // List view helpers
  const listDays = activeDay === 'All' ? ALL_DAYS : [activeDay];

  const dayRows = (day) => {
    const type = getDayType(day);
    const dayE = periods.filter(e => e.day === day).sort((a,b) => (a.period||0)-(b.period||0));
    return { type, entries: dayE, holidays: holidaysByDay[day]||[] };
  };

  if (loading) return (
    <div className="ttr"><style>{css}</style>
      <div className="ttr-loading"><div className="spinner"/>Loading timetable…</div>
    </div>
  );

  return (
    <div className="ttr"><style>{css}</style>
      <div className="ttr-hdr">
        <div>
          <div className="ttr-title">My Timetable</div>
          <div className="ttr-sub">Your scheduled periods across all assigned classes</div>
        </div>
        <div className="vtoggle">
          <button className={`vbtn${viewMode==='list'?' on':''}`} onClick={()=>setViewMode('list')}><ListBulletIcon/>List</button>
          <button className={`vbtn${viewMode==='grid'?' on':''}`} onClick={()=>setViewMode('grid')}><TableCellsIcon/>Grid</button>
        </div>
      </div>

      {periods.length === 0 && !loading ? (
        <div className="ttr-empty">No timetable periods assigned yet.</div>
      ) : (
        <>
          {/* LIST VIEW */}
          {viewMode === 'list' && (
            <>
              <div className="dtabs">
                <button className={`dtab${activeDay==='All'?' on':''}`} onClick={()=>setActiveDay('All')}>All</button>
                {ALL_DAYS.map(d => {
                  const type = getDayType(d);
                  const cls  = type==='off'||type==='sunday' ? ' off-tab' : type==='holiday' ? ' hol-tab' : '';
                  return (
                    <button key={d} className={`dtab${cls}${activeDay===d?' on':''}`} onClick={()=>setActiveDay(d)}>
                      {d.slice(0,3)}{type==='off'||type==='sunday'?' 📅':type==='holiday'?' 🎉':''}
                    </button>
                  );
                })}
              </div>

              <div className="panel">
                <div className="panel-hdr">
                  <div className="panel-title">
                    {activeDay==='All' ? 'All Periods' : activeDay}
                    <span style={{fontWeight:400,color:'#9094a8',marginLeft:8,fontSize:'.76rem'}}>
                      {periods.filter(e => activeDay==='All' || e.day===activeDay).length} period{periods.filter(e=>activeDay==='All'||e.day===activeDay).length!==1?'s':''}
                    </span>
                  </div>
                </div>
                <div className="lt-wrap" style={{overflowX:'auto'}}>
                  <table className="lt">
                    <thead><tr>
                      <th style={{minWidth:70,textAlign:'center'}}>Period</th>
                      {activeDay==='All' && <th style={{minWidth:100,textAlign:'center'}}>Day</th>}
                      <th style={{minWidth:150,textAlign:'center'}}>Time</th>
                      <th style={{minWidth:140,textAlign:'center'}}>Subject</th>
                      <th style={{minWidth:140,textAlign:'center'}}>Class / Section</th>
                    </tr></thead>
                    <tbody>
                      {listDays.map(day => {
                        const { type, entries: dayE, holidays: dayH } = dayRows(day);

                        const holRows = dayH.map((h,hi) => (
                          <tr key={`hol-${day}-${hi}`} className="hol-row">
                            <td colSpan={activeDay==='All'?5:4}>
                              <div style={{display:'flex',alignItems:'center',gap:8}}>
                                <span>🎉</span>
                                <span style={{fontWeight:700,color:'#065f46',fontSize:'.82rem'}}>{h.name}</span>
                                <span style={{fontSize:'.7rem',color:'#10c98e',fontFamily:"'JetBrains Mono',monospace"}}>{fmtShort(h.date)}</span>
                                <span style={{fontSize:'.68rem',color:'#9094a8',textTransform:'capitalize'}}>{h.type}</span>
                              </div>
                            </td>
                          </tr>
                        ));

                        if (type==='sunday' && !dayE.length && !dayH.length) return (
                          <tr key={`sun-${day}`} className="sun-row">
                            <td><span style={{color:'#c0c5dc',fontSize:'.72rem'}}>—</span></td>
                            {activeDay==='All'&&<td><span className="dbadge" style={{background:'#f0f2f7',color:'#9094a8'}}>{day}</span></td>}
                            <td></td><td><span style={{fontSize:'.74rem',fontWeight:600,color:'#9094a8'}}>📅 Sunday — Weekly Off</span></td><td></td>
                          </tr>
                        );

                        if (type==='off' && !dayE.length && !dayH.length) return (
                          <tr key={`off-${day}`} className="off-row">
                            <td><span style={{color:'#fecaca',fontSize:'.72rem'}}>—</span></td>
                            {activeDay==='All'&&<td><span className="dbadge" style={{background:'#fef2f2',color:'#ef4444'}}>{day}</span></td>}
                            <td></td><td>
                              <span style={{fontSize:'.74rem',fontWeight:600,color:'#ef4444'}}>
                                📅 Weekly Off
                                {satNote(day)&&<span style={{fontSize:'.68rem',color:'#f59e0b',marginLeft:6}}>({satNote(day)})</span>}
                              </span>
                            </td>
                          </tr>
                        );

                        if (!dayE.length && dayH.length) return <>{holRows}</>;

                        const showDay = activeDay==='All';
                        const toMins2 = (t) => { if(!t) return 0; const [h,m]=t.split(':').map(Number); return h*60+m; };
                        const periodRows = [];
                        let lunchDone = !lunchStart;
                        dayE.forEach((e,i) => {
                          if(!lunchDone && e.startTime && toMins2(e.startTime) >= toMins2(lunchEnd||lunchStart)){
                            periodRows.push(<tr key={`lunch-${day}`} style={{background:'#fffbeb'}}>
                              <td colSpan={showDay?5:4} style={{padding:'8px 16px',fontSize:'.76rem',fontWeight:600,color:'#92400e',textAlign:'center'}}>
                                🍽 Lunch Break — {lunchStart}{lunchEnd?` – ${lunchEnd}`:''}
                              </td>
                            </tr>);
                            lunchDone=true;
                          }
                          const color = subjColor(e.subject, uniqSubjs);
                          periodRows.push(<tr key={`${day}-${i}`}>
                            <td style={{textAlign:'center'}}><span className="pbadge">{e.period}</span></td>
                            {showDay&&<td style={{textAlign:'center'}}><span className="dbadge" style={{background:'#ecfdf5',color:'#10c98e'}}>{day}</span></td>}
                            <td style={{textAlign:'center'}}><span className="tcell">{e.startTime&&e.endTime?`${e.startTime} – ${e.endTime}`:e.startTime||e.endTime||'—'}</span></td>
                            <td style={{textAlign:'center'}}><span style={{display:'inline-flex',fontSize:'.76rem',fontWeight:600,padding:'4px 10px',borderRadius:99,background:`${color}18`,color}}>{e.subject}</span></td>
                            <td style={{textAlign:'center'}}><span style={{fontSize:'.78rem',color:'#4a5075'}}>{e.className}</span><span className="class-tag">Sec {e.section}</span></td>
                          </tr>);
                        });

                        return <>{holRows}{periodRows}</>;
                      })}
                      {/* Lunch break shown inline between periods */}
                    </tbody>
                  </table>
                </div>

                <div className="lt-cards">
                  {listDays.map(day => {
                    const { type, entries: dayE, holidays: dayH } = dayRows(day);
                    const cardCls = type==='sunday'?'day-card sun-card':type==='off'?'day-card off-card':type==='holiday'?'day-card hol-card':'day-card';
                    const badgeStyle = type==='sunday'?{background:'#f0f2f7',color:'#9094a8'}:type==='off'?{background:'#fef2f2',color:'#ef4444'}:type==='holiday'?{background:'#ecfdf5',color:'#10c98e'}:{background:'#ecfdf5',color:'#10c98e'};
                    const items = []; let lunchDone = !lunchStart;
                    dayE.forEach(e => {
                      if (!lunchDone && e.startTime) {
                        const [lh,lm] = lunchStart.split(':').map(Number);
                        const [ph,pm] = e.startTime.split(':').map(Number);
                        if (ph*60+pm >= lh*60+lm) { items.push({type:'lunch'}); lunchDone=true; }
                      }
                      items.push({type:'period',e});
                    });
                    if (lunchStart && !lunchDone && dayE.length) items.push({type:'lunch'});
                    return (
                      <div key={day} className={cardCls}>
                        <div className="day-card-hdr">
                          <span className="day-card-name">{day}</span>
                          <span className="day-card-badge" style={badgeStyle}>
                            {type==='off'?'📅 Weekly Off':type==='sunday'?'📅 Off':type==='holiday'?'🎉 Holiday':`${dayE.length} period${dayE.length!==1?'s':''}`}
                          </span>
                        </div>
                        {dayH.map((h,hi)=>(
                          <div key={hi} className="hol-pill">
                            <span style={{fontWeight:700,color:'#065f46'}}>🎉 {h.name}</span>
                            <span style={{fontSize:'.68rem',color:'#10c98e',marginLeft:6,fontFamily:"'JetBrains Mono',monospace"}}>{fmtShort(h.date)}</span>
                          </div>
                        ))}
                        {(type==='off'||type==='sunday')&&!dayE.length&&!dayH.length&&(
                          <div className={type==='sunday'?'sun-pill':'off-pill'}>
                            {type==='sunday'?'No classes — Sunday':'No classes scheduled'}
                            {satNote(day)&&<span style={{fontSize:'.68rem',color:'#f59e0b',marginLeft:6}}>({satNote(day)})</span>}
                          </div>
                        )}
                        {items.map((item,idx)=>{
                          if (item.type==='lunch') return (
                            <div key="lunch" className="lunch-pill">🍽 Lunch {lunchStart&&`${lunchStart}${lunchEnd?` – ${lunchEnd}`:''}`}</div>
                          );
                          const e=item.e; const col=subjColor(e.subject,uniqSubjs);
                          return (
                            <div key={idx} className="period-pill">
                              <span className="period-num">{e.period}</span>
                              <div className="period-info">
                                <div className="period-subj" style={{color:col}}>{e.subject}</div>
                                <div className="period-meta">
                                  {e.className} / {e.section}
                                  {e.startTime&&<span style={{marginLeft:6,fontFamily:"'JetBrains Mono',monospace",fontSize:'.68rem'}}>{e.startTime}{e.endTime?` – ${e.endTime}`:''}</span>}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* GRID VIEW */}
          {viewMode === 'grid' && (
            <div className="panel">
              <div className="panel-hdr">
                <div className="panel-title">Weekly Schedule</div>
              </div>
              <div className="gwrap">
                <table className="gt">
                  <thead>
                    <tr>
                      <th className="dh">Day</th>
                      {cols.map((col, ci) => {
                        if (col.t==='l') return (
                          <th key={`lh-${ci}`} className="lh">
                            🍽 Lunch
                            {lunchStart&&<div style={{fontSize:'.6rem',fontWeight:400,fontFamily:"'JetBrains Mono',monospace",marginTop:2}}>{lunchStart}{lunchEnd?` – ${lunchEnd}`:''}</div>}
                          </th>
                        );
                        return (
                          <th key={`ph-${col.p}`}>
                            <div>P{col.p}</div>
                            {periodTime(col.p)&&<div style={{fontSize:'.6rem',fontWeight:400,fontFamily:"'JetBrains Mono',monospace",color:'#b0b5cc',marginTop:2}}>{periodTime(col.p)}</div>}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {ALL_DAYS.map(day => {
                      const type    = getDayType(day);
                      const dayHols = holidaysByDay[day]||[];
                      const off     = type==='off'||type==='sunday';
                      const dcClass = type==='sunday'?'sun-dc':type==='off'?'off-dc':type==='holiday'?'hol-dc':'dc';

                      return (
                        <tr key={day}>
                          <td className={`dc ${dcClass}`}>
                            <div>{day}</div>
                            {type==='off'    &&<div style={{fontSize:'.62rem',color:'#ef4444',fontWeight:600,marginTop:2}}>📅 Off</div>}
                            {type==='sunday' &&<div style={{fontSize:'.62rem',color:'#9094a8',marginTop:2}}>📅 Off</div>}
                            {type==='holiday'&&<div style={{fontSize:'.62rem',color:'#10c98e',fontWeight:600,marginTop:2}}>🎉 Holiday</div>}
                            {satNote(day)    &&<div style={{fontSize:'.58rem',color:'#f59e0b',fontWeight:600,marginTop:1}}>{satNote(day)}</div>}
                          </td>
                          {off ? (
                            <td colSpan={cols.length||1} className={type==='sunday'?'sun-cell':'off-cell'}>
                              <div style={{fontSize:'.76rem',fontWeight:600,color:type==='sunday'?'#9094a8':'#ef4444'}}>
                                {type==='sunday'?'📅 Sunday — Weekly Off':'📅 Weekly Off'}
                                {satNote(day)&&<span style={{fontSize:'.68rem',color:'#f59e0b',marginLeft:6}}>({satNote(day)})</span>}
                              </div>
                              {dayHols.map((h,hi)=>(
                                <div key={hi} className="hol-chip" style={{marginTop:6}}>
                                  <span className="hol-chip-name">🎉 {h.name}</span>
                                  <span className="hol-chip-date">{fmtShort(h.date)}</span>
                                </div>
                              ))}
                            </td>
                          ) : cols.map((col, ci) => {
                            if (col.t==='l') return <td key={`lc-${ci}-${day}`} className="lc"><span style={{fontSize:'.8rem'}}>🍽</span></td>;
                            const cellE = gridMap[col.p]?.[day]||[];
                            if (!cellE.length) {
                              if (col.p===allPeriods[0] && dayHols.length) return (
                                <td key={`hc-${col.p}-${day}`} className="hol-cell">
                                  {dayHols.map((h,hi)=>(
                                    <div key={hi} className="hol-chip">
                                      <span className="hol-chip-name">🎉 {h.name}</span>
                                      <span className="hol-chip-date">{fmtShort(h.date)}</span>
                                    </div>
                                  ))}
                                </td>
                              );
                              return <td key={`ec-${col.p}-${day}`} className={dayHols.length?'hol-cell':'ec'} style={{color:'#d1d5db',fontSize:'.72rem',textAlign:'center',verticalAlign:'middle'}}>—</td>;
                            }
                            return (
                              <td key={`tc-${col.p}-${day}`}>
                                {cellE.map((e,ei) => {
                                  const c = subjColor(e.subject, uniqSubjs);
                                  return (
                                    <div key={ei} className="ge" style={{background:`${c}22`,borderLeft:`3px solid ${c}`,marginBottom:ei<cellE.length-1?4:0}}>
                                      <div className="ge-s" style={{color:c}}>{e.subject}</div>
                                      <div className="ge-t" style={{color:`${c}bb`}}>{e.className} / {e.section}</div>
                                      {e.startTime&&<div className="ge-tm" style={{color:`${c}88`}}>{e.startTime}{e.endTime?` – ${e.endTime}`:''}</div>}
                                    </div>
                                  );
                                })}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="mob-grid" style={{padding:'10px'}}>
                {ALL_DAYS.map(day => {
                  const type    = getDayType(day);
                  const dayHols = holidaysByDay[day]||[];
                  const dayE    = periods.filter(e=>e.day===day).sort((a,b)=>(a.period||0)-(b.period||0));
                  const cardCls = type==='sunday'?'day-card sun-card':type==='off'?'day-card off-card':type==='holiday'?'day-card hol-card':'day-card';
                  const badgeStyle = type==='sunday'?{background:'#f0f2f7',color:'#9094a8'}:type==='off'?{background:'#fef2f2',color:'#ef4444'}:type==='holiday'?{background:'#ecfdf5',color:'#10c98e'}:{background:'#ecfdf5',color:'#10c98e'};

                  const items = [];
                  let lunchDone = false;
                  dayE.forEach(e => {
                    if (!lunchDone && lunchStart && e.startTime) {
                      const [lh,lm] = lunchStart.split(':').map(Number);
                      const [ph,pm] = e.startTime.split(':').map(Number);
                      if (ph*60+pm >= lh*60+lm) { items.push({type:'lunch'}); lunchDone=true; }
                    }
                    items.push({type:'period',e});
                  });
                  if (lunchStart && !lunchDone && dayE.length) items.push({type:'lunch'});

                  return (
                    <div key={day} className={cardCls}>
                      <div className="day-card-hdr">
                        <span className="day-card-name">{day}</span>
                        <span className="day-card-badge" style={badgeStyle}>
                          {type==='off'?'📅 Weekly Off':type==='sunday'?'📅 Off':type==='holiday'?'🎉 Holiday':`${dayE.length} period${dayE.length!==1?'s':''}`}
                        </span>
                      </div>
                      {dayHols.map((h,hi)=>(
                        <div key={hi} className="hol-pill">
                          <span style={{fontWeight:700,color:'#065f46'}}>🎉 {h.name}</span>
                          <span style={{fontSize:'.68rem',color:'#10c98e',marginLeft:6,fontFamily:"'JetBrains Mono',monospace"}}>{fmtShort(h.date)}</span>
                        </div>
                      ))}
                      {(type==='off'||type==='sunday')&&!dayE.length&&(
                        <div className={type==='sunday'?'sun-pill':'off-pill'}>
                          {type==='sunday'?'No classes — Sunday':'No classes scheduled'}
                          {satNote(day)&&<span style={{fontSize:'.68rem',color:'#f59e0b',marginLeft:6}}>({satNote(day)})</span>}
                        </div>
                      )}
                      {items.map((item,idx)=>{
                        if (item.type==='lunch') return (
                          <div key="lunch" className="lunch-pill">🍽 Lunch {lunchStart&&`${lunchStart}${lunchEnd?` – ${lunchEnd}`:''}`}</div>
                        );
                        const e=item.e; const col=subjColor(e.subject,uniqSubjs);
                        return (
                          <div key={idx} className="period-pill">
                            <span className="period-num">{e.period}</span>
                            <div className="period-info">
                              <div className="period-subj" style={{color:col}}>{e.subject}</div>
                              <div className="period-meta">
                                {e.className} / {e.section}
                                {e.startTime&&<span style={{marginLeft:6,fontFamily:"'JetBrains Mono',monospace",fontSize:'.68rem'}}>{e.startTime}{e.endTime?` – ${e.endTime}`:''}</span>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TeacherTimetable;